import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { renderAgentMarkdown } from '@portal/app/shared/components/ai-assistant-panel/helpers/agent-markdown';
import { PortalToolCallComponent } from '@portal/app/shared/components/ai-assistant-panel/tools/tool-call.component';
import type { ThoughtStep } from '@portal/app/shared/components/ai-assistant-panel/types/chat-block.types';

/**
 * Chain-of-thought wrapper. All intermediate work (tool calls + reasoning
 * text) folds under a single collapsed-default row.
 *
 *   Historical:  ▸ Thought · 4 actions
 *   Live:        ⟳ Thinking · 2 actions   (spinner instead of chevron)
 *
 * Tools render inside the body as individual `<portal-tool-call>` cards.
 */
@Component({
  selector: 'portal-block-thought',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PortalToolCallComponent],
  templateUrl: './block-thought.component.html',
  host: { class: 'block' }
})
export class PortalBlockThoughtComponent {
  readonly steps = input.required<readonly ThoughtStep[]>();
  /** True while the agent is still emitting. Swaps chevron for a spinner. */
  readonly live = input(false);

  readonly #sanitizer = inject(DomSanitizer);

  protected readonly _expanded = signal(false);

  protected readonly label = computed(() => {
    let toolCount = 0;
    for (const s of this.steps()) {
      if (s.kind === 'tools') toolCount += s.tools.length;
    }
    const verb = this.live() ? 'Thinking' : 'Thought';
    if (toolCount === 0) return verb;
    return `${verb} · ${toolCount} ${toolCount === 1 ? 'action' : 'actions'}`;
  });

  protected renderReasoning(content: string): SafeHtml {
    return this.#sanitizer.bypassSecurityTrustHtml(
      renderAgentMarkdown(content)
    );
  }

  protected toggle(): void {
    this._expanded.update((v) => !v);
  }
}
